import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  signal,
  effect
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';

import {
  DialogRef,
  DialogService,
} from '@cyborg-monorepo/common/angular/cyborg-ui';
import {
  BadgeComponent,
  ButtonComponent,
  ButtonMenuComponent,
  DialogService as HyperkitDialogService,
  DropdownMenuItemComponent,
  ErrorComponent,
  TagComponent,
  TypographyComponent,
  BreadcrumbItem,
  BreadcrumbListComponent,
} from '@hyperkit';

import { Enums, Hunt } from '@cyborg-monorepo/hunter/common';
import { HuntService } from '../../shared/services/hunt.service';
import { HuntEditorComponent } from '../../../pages/hunt/details/components/hunt-editor';
import { HuntPackageListItem } from '../../../pages/hunt/details/components/packages';
import { HuntContextService } from '../../../pages/hunt/services/hunt-context.service';
import { EditHuntDialogComponent } from '../../hunt/hunt/edit-hunt-dialog/edit-hunt-dialog.component';
import { ArchiveHuntDialogComponent } from '../../hunt/overview/archive-hunt-dialog/archive-hunt-dialog.component';
import { CaptureDataDialogComponent } from '../../hunt/hunt/capture-data-dialog/capture-data-dialog.component';

@Component({
  standalone: true,
  selector: 'hunt-workbench',
  templateUrl: './hunt-workbench.component.html',
  styleUrls: ['hunt-workbench.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    RouterModule,
    ButtonComponent,
    TypographyComponent,
    HuntPackageListItem,
    BreadcrumbListComponent,
    HuntEditorComponent,
    BadgeComponent,
    TagComponent,
    ErrorComponent,
    ButtonMenuComponent,
    DropdownMenuItemComponent,
  ],
})
export class HuntWorkbenchComponent implements OnInit {
  readonly huntContext = inject(HuntContextService);
  readonly route = inject(ActivatedRoute);
  readonly huntService = inject(HuntService);
  readonly router = inject(Router);
  readonly dialogService = inject(DialogService);
  readonly hyperkitDialogService = inject(HyperkitDialogService);
  readonly snackBarService = inject(MatSnackBar);

  public breadcrumbs = signal<BreadcrumbItem[]>([]);

  @Input() public hunt: Hunt | null = null;
  @Output() public updatedHunt = new EventEmitter<Hunt>();

  public form: FormGroup;
  public error: string;

  public get PriorityEnum() {
    return Enums.PriorityEnum;
  }

  public get StatusEnum() {
    return Enums.StatusEnum;
  }

  public get OutcomeEnum() {
    return Enums.OutcomeEnum;
  }

  constructor() {
    // ✅ Hydrate hunt from resolved route if context doesn't already have it
      const resolvedHunt = this.route.snapshot.data['hunt'] as Hunt | null;
      const contextHunt = this.huntContext.state.hunt();

      if (resolvedHunt && !contextHunt?.id) {
        this.huntContext.state.updateHunt(resolvedHunt);
        this.huntContext.editor.load(resolvedHunt);
      }

      effect(() => {
      this.breadcrumbs.set([
        { text: 'Hunt', selected: false, link: '/hunt/hunts' },
        { text: this.huntContext.state.name(), selected: true },
      ]); 
      });

  }

  ngOnInit(): void {}

  editHunt(): void {
    if (!this.hunt) return;
    const dialogRef: DialogRef = this.dialogService.open(EditHuntDialogComponent, {
      data: this.hunt,
    });

    dialogRef.afterClosed().subscribe((updatedHunt: Hunt) => {
      if (updatedHunt) this.hunt = updatedHunt;
    });
  }

  archiveHunt(): void {
    if (!this.hunt) return;
    const dialogRef: DialogRef = this.dialogService.open(ArchiveHuntDialogComponent, {
      data: this.hunt,
    });

    dialogRef.afterClosed().subscribe((hunt: Hunt) => {
      if (hunt) {
        this.router.navigate(['/hunt/hunts']);
        this.snackBarService.open('Archived ' + hunt.name, undefined, {
          duration: 5000,
        });
      }
    });
  }

  closeHunt(): void {
    this.huntContext.editor.updateStatus(Enums.StatusEnum.DONE);
    this.huntContext.editor.submit();
    this.router.navigate(['hunt-redesign', this.huntContext.state.id(), 'edit-report']);
  }

  handleMenuAction(event: { value: 'update' | 'archive' }): void {
    switch (event.value) {
      case 'update':
        this.editHunt();
        break;
      case 'archive':
        this.archiveHunt();
        break;
    }
  }
}
